import { useContext } from 'react'
import { CustomCriterionValidationError } from '../../../data/gql-gen/fieldwork/graphql'
import {
  FieldworkAudienceType,
  MemberSettingCode
} from '../../../data/model/audience'
import {
  AudienceQuestionLogic,
  DisplayLogicDraft
} from '../../../data/model/audienceQuestionLogic'
import { Operator } from '../../../data/model/common'
import { StandardCriterionValidationError } from '../../../data/model/fieldwork'
import { useSetAudienceQuestionLogicV2 } from '../../../hooks/audience/setAudienceQuestionLogicV2'
import { useGetAudienceQuestionLogic } from '../../../hooks/audience/useGetAudienceQuestionLogic'
import useGetDraftAudience from '../../../hooks/audience/useGetDraftAudience'
import AudienceContext from '../Audience.context'
import {
  getAudienceMemberQuestionId,
  getSurveyQuestionLabels,
  inScreenerOrder
} from '../audienceOrder'
import {
  getCustomAudienceValidationErrors,
  getIsEnabledInSettings,
  getStandardAudienceValidationErrors
} from '../utils'
import { sourceQuestionsFor, toDraft } from './screenerDisplayLogic.utils'

export const useScreenerDisplayLogic = (questionId: string | undefined) => {
  const { draftAudience } = useGetDraftAudience()
  const logicByQuestionId = useGetAudienceQuestionLogic()
  const { audienceState } = useContext(AudienceContext)
  const setAudienceQuestionLogic = useSetAudienceQuestionLogicV2()

  const ordered = inScreenerOrder(draftAudience?.members)
  const labels = getSurveyQuestionLabels(ordered)
  const index = ordered.findIndex(
    (member) => getAudienceMemberQuestionId(member) === questionId
  )
  const member = index === -1 ? undefined : ordered[index]

  const logic: AudienceQuestionLogic =
    logicByQuestionId.get(questionId ?? '') ?? []
  const questions = sourceQuestionsFor(draftAudience?.members, questionId ?? '')

  // Options carry placeholder quotas from creation, so only the setting says whether quotas are live.
  const hasQuotas =
    !!member &&
    getIsEnabledInSettings(
      member.memberItem.settingValues,
      MemberSettingCode.Quotas
    )

  const errors = audienceState.validationErrors
  const hasInvalidSource =
    member?.memberType === FieldworkAudienceType.Standard
      ? !!getStandardAudienceValidationErrors(
          errors,
          member.memberItem.criterion.code
        )?.standardCriterionErrors.includes(
          StandardCriterionValidationError.InvalidDisplayLogicSourceError
        )
      : !!getCustomAudienceValidationErrors(
          errors,
          member?.memberItem.questionLk ?? ''
        )?.customCriterionErrors.includes(
          CustomCriterionValidationError.InvalidDisplayLogicSourceError
        )

  return {
    label: index === -1 ? '' : labels[index] ?? '',
    isTargeting: member?.memberType === FieldworkAudienceType.Standard,
    hasQuotas,
    hasInvalidSource,
    logic,
    draft:
      logic.length > 0 ? toDraft(logic) : { rows: [], operator: Operator.And },
    questions,
    save: (draft: DisplayLogicDraft) =>
      questionId ? setAudienceQuestionLogic(questionId, draft) : undefined,
    remove: () =>
      questionId ? setAudienceQuestionLogic(questionId, undefined) : undefined
  }
}
